import { Form, Formik, FormikProps, FormikValues } from 'formik'
import React, { useEffect, useState } from 'react'
import * as Yup from 'yup'
import { FbButton, FbCollapse, FbGrow, Icons, LoadingIndicator } from '@fabricks/ui-components'
import { FormikHelpers } from 'formik/dist/types'
import styled from 'styled-components'

export interface FormElementVisibleConfig {
    target: string
    isVisible: (target: string, value: string) => boolean
}

export interface FormElementFilterConfig {
    target: string
    hasFilter: (target: string, value: string) => object
}

export interface FormElementValidationConfig {
    type: 'string' | 'number' | 'boolean' | 'date'
    rules: {name: string; params: any[]}[]
}

export interface FormElementConfig {
    childProps?: any
    component: (props: FormElementProps) => JSX.Element | null
    visibility?: FormElementVisibleConfig[]
    filter?: FormElementFilterConfig[]
    validation?: FormElementValidationConfig
}

export interface FormElementProps {
    name: string
    config: FormElementConfig
    childProps: any
}

export interface FormGeneratorConfig {
    [key: string]: FormElementConfig
}

export interface FormFeedback {
    message: string
    type?: 'success' | 'error' | 'warning'
    timeout?: number
}

export interface FormGeneratorProps {
    id: string
    submitLabel?: string
    initialValues: FormikValues
    config: FormGeneratorConfig
    noSubmit?: boolean
    willSubmit?: (values: FormikValues, actions: FormikHelpers<FormikValues>) => any
    onSubmit?: (
        values: unknown,
        actions: FormikHelpers<FormikValues>,
        setFeedback: React.Dispatch<React.SetStateAction<FormFeedback>>
    ) => void
    onRenderSubmitButton?: () => JSX.Element
}

const ProgressContainer = styled.div`
    margin: 15px 0px 0px 0px;
`

export const FormGenerator = (props: FormGeneratorProps) => {
    const { config, submitLabel, willSubmit, onSubmit, id, initialValues, noSubmit, onRenderSubmitButton } = props
    const [feedback, setFeedback] = useState<FormFeedback>({ message: '' })

    const getValidationYup = () => {
        const yupSchema = {}
        Object.keys(config).forEach((elementName: string) => {
            const configForElement: FormElementConfig = config[elementName]
            if (!configForElement.validation) {
                return null
            }
            const { type, rules } = configForElement.validation
            rules.forEach((rule: {name: string; params: any}) => {
                const { name, params } = rule

                // @ts-ignore
                if (!Yup[type]()) {
                    return null
                }
                // @ts-ignore
                const formTypeValidator = Yup[type]()
                // @ts-ignore
                const formElementValidator = formTypeValidator[name]

                if (formElementValidator) {
                    // @ts-ignore
                    yupSchema[elementName] = formTypeValidator[name](...params)
                }
            })
        })
        return Yup.object().shape(yupSchema)
    }

    const validate = (values: FormikValues) => {
        const yup = getValidationYup()
        yup.validate(values, { abortEarly: false })
            .then(res => {
                console.info(res)
            })
            .catch(errors => {
                console.error(errors)
            })
    }

    const renderFeedback = () => {
        if (feedback.timeout) {
            setTimeout(() => {
                setFeedback({ message: '' })
            }, feedback.timeout)
        }

        const getIconAndColor = (feedbackType: any) => {
            switch (feedbackType) {
                case 'success':
                    return { icon: Icons.Check, color: 'limegreen' }
                case 'error':
                    return { icon: Icons.Error, color: '#d32f2f' }
                case 'warning':
                    return { icon: Icons.Warning, color: '#ffa726' }
                default:
                    return { color: 'black' }
            }
        }

        const iconAnColor = getIconAndColor(feedback.type)
        const usedIcon = iconAnColor.icon ? <iconAnColor.icon style={{ float: 'left' }} /> : null

        return (
            <div style={{ color: iconAnColor.color, paddingTop: '3px' }}>
                {usedIcon}
                <span style={{ paddingLeft: '7px', paddingTop: '4px', float: 'left' }}>{feedback.message}</span>
            </div>
        )
    }

    const renderForm = (id: string) => (formProps: FormikProps<any>) => {
        const renderSubmitButton = () => {
            if (noSubmit) {
                return null
            }

            if (onRenderSubmitButton) {
                return onRenderSubmitButton()
            }

            return (
                <FbButton
                    disabled={formProps.isSubmitting}
                    variant="contained"
                    color="primary"
                    type="submit"
                    style={{ float: 'right' }}>
                    {submitLabel || 'Send'}
                </FbButton>
            )
        }

        const formElements = Object.keys(config).map((key: string) => {
            const formElement: FormElementConfig = config[key]
            const { childProps } = formElement
            return (
                <formElement.component key={`${id}-${key}`} name={key} config={formElement} childProps={childProps} />
            )
        })

        return (
            <Form>
                {formElements}
                {renderSubmitButton()}
                <FbGrow in={feedback.type !== undefined} timeout={500}>
                    {renderFeedback()}
                </FbGrow>
                <div style={{ clear: 'both' }} />
                <FbCollapse in={formProps.isSubmitting} collapsedHeight={0}>
                    <ProgressContainer>
                        <LoadingIndicator color="secondary" variant="indeterminate" value={10} valueBuffer={3} />
                    </ProgressContainer>
                </FbCollapse>
            </Form>
        )
    }

    const submit = (values: FormikValues, actions: FormikHelpers<FormikValues>) => {
        setFeedback({ message: '' })
        const dataForTransmission = willSubmit ? willSubmit(values, actions) : values
        if (onSubmit) {
            onSubmit(dataForTransmission, actions, setFeedback)
        }
    }

    return (
        <Formik
            key={id}
            initialValues={initialValues}
            enableReinitialize={true}
            validationSchema={getValidationYup}
            onSubmit={submit}>
            {(formProps: FormikProps<any>) => {
                return renderForm(id)(formProps)
            }}
        </Formik>
    )
}
